import { TableBody, TableCell, TableContainer, TableRow as MuiTableRow } from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import MuiTable from "@material-ui/core/Table";
import useText from "constants/text";
import { Hub } from "generated/graphql";
import React from "react";
import "styled-components/macro";
import { Action } from "ui/ActionGroup";
import { getTableSorting, makeSearch, stablilizeTableSorting } from "utils";
import { TableFooter } from "./Footer";
import { TableHeader } from "./Header";
import { TableLoader } from "./Loader";
import { TableRow } from "./Row";
import { EmptyTableContent, EmptyText } from "./styles";

export interface Column<RowData> {
  title: string;
  field: string;
  render?(data: RowData): React.ReactNode;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
    },
    paper: {
      width: "100%",
      marginBottom: theme.spacing(1),
    },
    table: {
      width: "100%",
      borderCollapse: "separate",
      borderVerticalSpacing: "10px",
      background: "#ffff",
      [theme.breakpoints.down("xs")]: {
        width: "360vw",
      },
      "& .MuiTableCell-stickyHeader": {
        [theme.breakpoints.down("xs")]: {},
      },
      "&.MuiTableContainer-root": {
        [theme.breakpoints.down("xs")]: {},
      },
    },
    empty: {
      textAlign: "center",
      marginLeft: "auto",
      marginRight: "auto",
      width: "100%",
    },
  })
);

// trie function
export function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

export function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export function stableSort<T extends any>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export interface TableProps<RowData, RowDataKey extends keyof RowData> {
  onRowClick?(data: RowData): void;
  onRowMenuClick?(key: string, row: RowData): void;
  onRowChecked?(data: RowData): void;
  onAllRowChecked?(data: Array<string>): void;
  rowsIdentifier?: RowDataKey;
  data: RowData[];
  columns: Array<Column<RowData>>;
  isLoading?: boolean;
  hasSelection?: boolean;
  orderByIdentifier?: RowDataKey;
  rowsPerPageOptions?: number[];
  defaultOrder?: RowDataKey;
  asAction?: boolean;
  isShowMenu?: boolean;
  showMenu?: boolean;
  advancedControl?: boolean;
  setRowsPerPage?(length: number): void;
  rowsPerPage?: number;
  currentPage?: number;
  countTotal?: number;
  page?: number;
  onChangePage?(event: unknown, newPage: number): void;
  rowScanned?: string[];
  isDisabledChecked?: boolean;
  tableHeight?: string;
  tableWidth?: string;
  searchTerm?: string;
  columnsToSearchIn?: string[];
  isDisabledFooter?: boolean;
  isSelected?: boolean;
  actionTriggers?: (actionName: string) => void;
  multipleActions?: Action[];
  selectedOrder?: Order;
  statusKey?: string;
  selectedRows?: string[];
  hasAllSelection?: boolean;
  onRowClick?(data: RowData, event?: React.MouseEvent<unknown, MouseEvent>): void;
  handleGetReceiverHub?(data: Hub, rowId: string): void;
  handleGetRowsChecked?(data: string[]): void;
  onRowCheckedRow?(data: string[]): void;
  hasRadioButton?: boolean;
  onRadioButtonSelected?(data: RowData): void;
  selectedRadionButton?: string;
  onRowEdit?(data: RowData): void;
  isCurrentRow?(data: RowData): boolean;
  type?: any;
}

export function Table<RowData, RowDataKey extends keyof RowData>({
  columns = [],
  isLoading = false,
  hasSelection = false,
  onRowClick,
  rowsIdentifier,
  data = [],
  onAllRowChecked,
  onRowChecked,
  orderByIdentifier = rowsIdentifier,
  rowsPerPageOptions,
  defaultOrder,
  asAction = true,
  onRowMenuClick,
  isShowMenu,
  showMenu,
  rowsPerPage,
  setRowsPerPage,
  currentPage,
  countTotal,
  onChangePage,
  rowScanned,
  isDisabledChecked,
  searchTerm,
  isDisabledFooter = false,
  multipleActions,
  tableWidth,
  tableHeight,
  selectedRows,
  hasAllSelection,
  onRowCheckedRow,
  handleGetReceiverHub,
  handleGetRowsChecked,
  hasRadioButton,
  onRadioButtonSelected,
  selectedRadionButton,
  onRowEdit,
  isCurrentRow,
  type,
}: TableProps<RowData, RowDataKey>) {
  const { text } = useText();
  const styles = useStyles();
  const [selected, setSelected] = React.useState<string[]>(selectedRows || []);
  const [order, setOrder] = React.useState<Order>("desc");
  const [orderBy, setOrderBy] = React.useState<keyof RowData>(orderByIdentifier);
  const [page, setPage] = React.useState(0);
  const [row, setRow] = React.useState(null);
  React.useEffect(() => {
    if (defaultOrder) {
      setOrderBy(defaultOrder);
    }
  }, [data, defaultOrder]);
  React.useEffect(() => {
    if (selectedRows) setSelected(selectedRows);
  }, [selectedRows]);

  const handlerSelectAll = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (hasAllSelection) {
        if (event.target.checked) {
          const newSelecteds = data.map((currentCell: any) => currentCell[rowsIdentifier]);
          setSelected(newSelecteds);
          onAllRowChecked && onAllRowChecked(newSelecteds);
          onRowCheckedRow && onRowCheckedRow(newSelecteds);
        } else {
          setSelected([]);
          onAllRowChecked && onAllRowChecked([]);
          onRowCheckedRow && onRowCheckedRow([]);
        }
      }
    },
    [data, rowsIdentifier, onAllRowChecked, onRowCheckedRow]
  );

  const handleRowSelectClick = React.useCallback(
    (event: React.MouseEvent<unknown>, id: string) => {
      const selectedIndex = selected.indexOf(id);

      let newSelected: string[] = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, id);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
      }
      setSelected(newSelected);
      onRowCheckedRow && onRowCheckedRow(newSelected);
      (!selectedRows || !onRowChecked || !onRowCheckedRow) && setSelected(newSelected);
    },
    [selected, onRowChecked, onRowCheckedRow]
  );

  const totalRowsSelectedCount = React.useMemo(() => selected?.length, [selected]);
  const totalDataToShowCount = React.useMemo(() => data?.length || -1, [data]);
  const isRowSelected = React.useCallback(
    (name: string) => selected.includes(name),

    [selected]
  );
  function selectAllCheck() {
    return rowScanned && rowScanned?.length > 0 && totalDataToShowCount === rowScanned?.length;
  }

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof RowData) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  React.useEffect(() => {
    setPage(0);
  }, [data]);
  const filterRows = React.useMemo(() => (searchTerm ? makeSearch(data, searchTerm) : []), [data, searchTerm]);

  const rows = React.useMemo(() => data, [data]);

  const isUserFiltering = Boolean(searchTerm) && rows;
  const dataToShow = isUserFiltering ? filterRows : rows;

  const isDataToShowNotEmpty = dataToShow && Array.isArray(dataToShow) && dataToShow?.length !== 0;

  const sortedRows = React.useMemo(() => {
    let result;
    if (countTotal) {
      result =
        isDataToShowNotEmpty &&
        stablilizeTableSorting(dataToShow, getTableSorting(order, orderBy)).slice(0, rowsPerPage);
    } else {
      result =
        isDataToShowNotEmpty &&
        stablilizeTableSorting(dataToShow, getTableSorting(order, orderBy)).slice(
          page * rowsPerPage,
          page * rowsPerPage + rowsPerPage
        );
    }
    return result;
  }, [dataToShow, order, orderBy, page, rowsPerPage, isDataToShowNotEmpty]);
  React.useEffect(() => {
    handleGetRowsChecked && handleGetRowsChecked(selected);
  }, [selected]);
  return (
    <>
      <TableContainer style={{ height: tableHeight, width: tableWidth }}>
        <MuiTable size="medium" stickyHeader aria-label="sticky table" className={styles.table}>
          <TableHeader
            columns={columns}
            onRequestSort={handleRequestSort}
            hasSelection={hasSelection}
            asAction={asAction}
            onAllRowChecked={handlerSelectAll}
            rowCount={totalDataToShowCount}
            selectedRowsCount={totalRowsSelectedCount}
            orderBy={orderBy}
            order={order}
            isDisabledChecked={selectAllCheck() ? false : isDisabledChecked}
            hasRadioButton={hasRadioButton}
          />
          {isLoading ? (
            <TableLoader rowDepth={5} cellDepth={columns?.length + 1} />
          ) : (
            <TableBody
              css={`
                background-color: transparent;
              `}
            >
              {isDataToShowNotEmpty &&
                sortedRows?.map((rowContent, index) => (
                  <TableRow
                    key={`${rowContent[rowsIdentifier]}${index}`}
                    columns={columns}
                    rowContent={rowContent as any}
                    rowsIdentifier={rowsIdentifier}
                    hasSelection={hasSelection}
                    onRowClick={onRowClick}
                    onRowChecked={onRowChecked}
                    multipleActions={multipleActions}
                    selectRowsCount={totalRowsSelectedCount}
                    rowsCount={totalDataToShowCount}
                    isRowSelected={isRowSelected}
                    handleRowSelectClick={handleRowSelectClick}
                    isCurrentRow={isCurrentRow}
                    onRowMenuClick={onRowMenuClick}
                    isShowMenu={isShowMenu}
                    showMenu={showMenu}
                    rowColor={rowContent?.type || type}
                    onRowCheckedRow={handleRowSelectClick}
                    handleGetReceiverHub={handleGetReceiverHub}
                    hasRadioButton={hasRadioButton}
                    onRadioButtonSelected={onRadioButtonSelected}
                    selectedRadionButton={selectedRadionButton}
                    onRowEdit={onRowEdit}
                    rowScanned={rowScanned}
                  />
                ))}
              {!isDataToShowNotEmpty && searchTerm && (
                <MuiTableRow>
                  <TableCell colSpan={columns?.length + 1}>
                    <EmptyTableContent>
                      {text.all.no_result_found} <i className="highlighted">{searchTerm}</i>
                    </EmptyTableContent>
                  </TableCell>
                </MuiTableRow>
              )}
            </TableBody>
          )}
        </MuiTable>
        {!isLoading && data?.length === 0 && <EmptyText> {text.all.empty_table} </EmptyText>}
      </TableContainer>
      {isDataToShowNotEmpty && !isDisabledFooter && (
        <TableFooter
          rowsPerPageOptions={rowsPerPageOptions || [10]}
          handleChangePage={onChangePage || handleChangePage}
          count={countTotal || data?.length}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          page={currentPage || page}
          rowsPerPage={rowsPerPage}
        />
      )}
    </>
  );
}
